type AbTestsResponseFailure =
  | 'http_error'
  | 'body_read_error'
  | 'empty_body'
  | 'unexpected_content_type'
  | 'invalid_json'
  | 'invalid_payload';

type AbTestsResponseDetails = {
  endpoint: string;
  status: number;
  contentType: string;
  responseType: string;
  redirected: boolean;
  bodyCharacters: number | null;
};

export class AbTestsResponseError extends Error {
  name = 'AbTestsResponseError';

  constructor(
    public readonly reason: AbTestsResponseFailure,
    public readonly details: AbTestsResponseDetails,
  ) {
    super(`Invalid A/B tests response: ${reason}`);
  }
}

const isJsonContentType = (contentType: string | null) => {
  const mediaType = contentType?.split(';')[0].trim().toLowerCase();
  return (
    mediaType === 'application/json' ||
    (mediaType?.startsWith('application/') && mediaType.endsWith('+json'))
  );
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isAbTestsResponse = (value: unknown) => {
  if (!isRecord(value)) return false;
  if (Object.keys(value).length === 0) return true;

  return (
    typeof value.segment_name === 'string' &&
    value.segment_name.trim().length > 0 &&
    Array.isArray(value.tests) &&
    value.tests.every(
      (test) =>
        isRecord(test) &&
        (test.test_name == null || typeof test.test_name === 'string') &&
        (test.case_name == null || typeof test.case_name === 'string'),
    )
  );
};

// A/B response diagnostics contain neither the payload nor user query parameters.
export const readAbTestsResponse = async (
  response: Response,
  requestUrl: string,
) => {
  const url = new URL(requestUrl);
  const details: AbTestsResponseDetails = {
    endpoint: `${url.origin}${url.pathname}`,
    status: response.status,
    contentType: response.headers.get('Content-Type') ?? '',
    responseType: response.type,
    redirected: response.redirected,
    bodyCharacters: null,
  };
  let body: string;
  try {
    body = await response.text();
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') throw error;
    throw new AbTestsResponseError('body_read_error', details);
  }
  details.bodyCharacters = body.length;

  if (!response.ok) throw new AbTestsResponseError('http_error', details);
  if (!body.trim()) throw new AbTestsResponseError('empty_body', details);
  if (!isJsonContentType(details.contentType)) {
    throw new AbTestsResponseError('unexpected_content_type', details);
  }

  let data;
  try {
    data = JSON.parse(body);
  } catch {
    throw new AbTestsResponseError('invalid_json', details);
  }
  if (!isAbTestsResponse(data)) {
    throw new AbTestsResponseError('invalid_payload', details);
  }
  return data;
};
